import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate, formatDateTime } from "../../api/dates";
import { useClearRedList, useRedList, useRemoveRedListUpload, useUploadRedList } from "../../api/delivery";
import { useSiteSummary } from "../../api/sites";
import { Card } from "../../components/ui";
import "../../styles/sites.css";
import ReportView from "../ingest/ReportView";
import SectionTabs from "../sites/SectionTabs";

/** The client's Red List: download the standard template, upload a filled-in
 * one, see what each upload put on, take an upload -- or everything -- off.
 * Laid out as the two steps the office actually takes, then what is on the
 * list now, so the screen reads in the order the work happens. */
export default function RedListPage() {
  const context = useRedList();
  const summary = useSiteSummary();
  const upload = useUploadRedList();
  const remove = useRemoveRedListUpload();
  const clear = useClearRedList();
  const [contractId, setContractId] = useState("");
  const input = useRef<HTMLInputElement>(null);
  const data = context.data;
  const contracts = data?.contracts ?? [];
  const contract = contractId || (contracts.length === 1 ? String(contracts[0].id) : "");
  const uploads = data?.uploads ?? [];

  return (
    <div className="page-bleed site-section">
      <SectionTabs />
      <div className="site-page">
        {context.isError ? (
          <p className="notice bad" role="alert">
            Could not load the Red List: {context.error.message}
          </p>
        ) : null}
        <div className="redlist-steps">
          <div className="card card-pad redlist-step">
            <span className="section-label">Step 1</span>
            <strong>Get the standard template</strong>
            <p>The City fills it in with works orders and priority dates.</p>
            {data ? (
              <a className="btn btn-secondary" href={data.template_url}>
                Download template (.xlsx)
              </a>
            ) : null}
          </div>
          <div className="card card-pad redlist-step">
            <span className="section-label">Step 2</span>
            <strong>Upload the filled-in list</strong>
            <p>Each row puts a site on the Red List at its priority.</p>
            {contracts.length > 1 ? (
              <label className="redlist-contract">
                Contract
                <select className="select" value={contract} onChange={(e) => setContractId(e.target.value)}>
                  <option value="">Choose…</option>
                  {contracts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : contracts.length === 1 ? (
              <p>Contract {contracts[0].code || contracts[0].name}.</p>
            ) : null}
            <input
              ref={input}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && contract !== "") {
                  upload.mutate({ contractId: Number(contract), file });
                }
                e.target.value = "";
              }}
            />
            <button
              type="button"
              className="btn btn-primary"
              disabled={upload.isPending || contract === ""}
              onClick={() => input.current?.click()}
            >
              {upload.isPending ? "Reading…" : "Choose file and upload"}
            </button>
          </div>
          <div className="card card-pad redlist-step">
            <span className="section-label">On the list now</span>
            <span className="redlist-count">{summary.data?.red_list ?? "—"}</span>
            <p>
              sites, from {uploads.length} upload{uploads.length === 1 ? "" : "s"}
            </p>
            <Link className="go-link" to="/sites?red_list=on">
              See them in Sites →
            </Link>
          </div>
        </div>

        {upload.isError ? (
          <p className="notice bad" role="alert">
            {upload.error.message}
          </p>
        ) : null}
        {upload.data?.report ? (
          <Card title="What the upload did">
            <ReportView report={upload.data.report} />
          </Card>
        ) : null}

        <Card
          title="Uploads"
          flush
          actions={
            <button
              type="button"
              className="btn btn-danger"
              disabled={clear.isPending || uploads.length === 0}
              onClick={() => {
                if (window.confirm("Take every site off the Red List? Re-uploading the file puts them back.")) {
                  clear.mutate();
                }
              }}
            >
              Clear the whole Red List…
            </button>
          }
        >
          {remove.isError ? <p className="notice bad site-register-note">{remove.error.message}</p> : null}
          {clear.isError ? <p className="notice bad site-register-note">{clear.error.message}</p> : null}
          {context.isPending ? (
            <p className="empty-state">Loading uploads…</p>
          ) : uploads.length === 0 ? (
            <p className="empty-state">Nothing on the Red List. Upload the City's list to put sites on it.</p>
          ) : (
            <div className="site-table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Uploaded</th>
                    <th className="num">Sites</th>
                    <th>Priority dates</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {uploads.map((row) => (
                    <tr key={row.source}>
                      <td className="mono">{row.filename}</td>
                      <td className="muted">{formatDateTime(row.uploaded_at)}</td>
                      <td className="num">{row.sites}</td>
                      <td className="muted">
                        {row.earliest ? formatDate(row.earliest) : "—"}
                        {row.latest && row.latest !== row.earliest ? ` – ${formatDate(row.latest)}` : ""}
                      </td>
                      <td className="num">
                        <button
                          type="button"
                          className="row-button"
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(row.source)}
                          aria-label={`Take ${row.filename} off the Red List`}
                        >
                          Take off
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
